import { logger } from '@adornis/base/logging.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { LogistikOrder, LogistikOrderDeliveryState } from '@chancenwerk/db/LogistikOrder.js';
import { LogistikOrderAPI } from '@chancenwerk/db/api/LogistikOrderAPI.js';
import { LogistikOrderDeliveryStateEnum, LogistikShippingStatus } from '@chancenwerk/db/enums/LogistikEnums.js';
import { LogistikOrderFilter } from '@chancenwerk/db/filter/LogistikOrderFilter.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';

/**
 * @customElement logistik-dialog-delivery-notices
 */
@customElement('logistik-dialog-delivery-notices')
export class LogistikDialogDeliveryNotices extends ChromiumDialog<void> {
  static override get element_name() {
    return 'logistik-dialog-delivery-notices';
  }

  @state() private readonly _filter = new LogistikOrderFilter({});
  @state() private readonly _isLoading = new RXController(this, false);

  override heading = 'Empfangsbestätigung';

  override connectedCallback(): void {
    super.connectedCallback();
    this._filter.status = [LogistikShippingStatus.SHIPPED];
  }

  override content() {
    return html`
      <x-data-view
        .options=${{
          class: LogistikOrder,
          operation: LogistikOrderAPI.subscribePaginated,
          selectionSet: LogistikOrder.allFields,
          view: new View({ filter: this._filter }),
          emptyState: () => this._getEmptyState(),
          isSortable: true,
          onlyShowLastTranslation: true,
          customColumns: this._columnDefinitions,
        } as DataViewOptions<typeof LogistikOrder>}
      ></x-data-view>
    `;
  }

  private _getEmptyState() {
    return html`
      <cr-flex w100 h100 center crossaxis-center>
        <cr-text> No orders found </cr-text>
      </cr-flex>
    `;
  }

  private readonly _columnDefinitions: ColumnDefinition<typeof LogistikOrder>[] = [
    {
      columnName: 'Status',
      fieldName: 'deliveryStatus',
      columnWidth: 'max-content',
      getFieldMapping(entityInstance) {
        return entityInstance.deliveryStatus?.status;
      },
    },
    {
      columnName: 'Bestellnummer',
      fieldName: 'orderNumber',
      getFieldMapping(entityInstance) {
        return entityInstance.orderNumber;
      },
    },
    {
      columnName: 'Letzte Anfrage',
      fieldName: 'deliveryStateDateChanged',
      getFieldMapping(entityInstance) {
        return entityInstance.deliveryStatus?.updatedAt?.toLocaleString(DateTime.DATETIME_SHORT) ?? '-';
      },
    },
    {
      customID: '_button',
      columnWidth: 'max-content',
      renderItem: entityInstance => {
        return html`
          <cr-flex crossaxis-center>
            ${entityInstance.deliveryStatus?.status === LogistikOrderDeliveryStateEnum.RECEIVED
              ? nothing
              : html`
                  <cr-button
                    ${css({
                      backgroundColor: 'white',
                      color: '#E28807',
                      boxShadow: 'none',
                      justifySelf: 'end',
                    })}
                    .loading=${this._isLoading.value}
                    icon="send"
                    @click=${async () => {
                      this._isLoading.next(true);
                      try {
                        await LogistikOrder.sendDeliveryStatusRequestMail(entityInstance)();
                        void ChromiumSnackbar.show('Anfrage wurde versendet');
                        if (!entityInstance.deliveryStatus)
                          entityInstance.deliveryStatus = new LogistikOrderDeliveryState({
                            status: LogistikOrderDeliveryStateEnum.REQUESTED,
                            updatedAt: DateTime.now(),
                          });
                      } catch (e) {
                        logger.error(
                          { err: e },
                          'Es gab ein Problem beim erneuten Abfragen des Lieferungsstatus per Dialog',
                        );
                        void ChromiumDialog.alert(
                          'Es gab ein Problem beim erneuten Abfragen des Lieferungsstatus.' +
                            (baseConfig.get('DEPLOYMENT') !== 'production' ? ` ${e.message}` : ''),
                        );
                      }
                      this._isLoading.next(false);
                    }}
                  >
                    Empfang abfragen
                  </cr-button>
                `}
          </cr-flex>
        `;
      },
    },
  ];
}
